// The component library: what a page may contain. Each component comes from
// the app's source, built with it, and declares its props, whether it holds
// text and whether it holds other nodes. An edit can only name one of these,
// or a component made on the page from them (library.ts).
// meets: code/from-source, code/rebuild
import type { ComponentChildren, VNode } from "preact";
import { useState } from "preact/hooks";
import { safeHref } from "./href";

export type Prop =
  | { kind: "string"; default: string; max?: number }
  | { kind: "number"; default: number; min?: number; max?: number }
  | { kind: "enum"; default: string; options: string[] }
  | { kind: "boolean"; default: boolean };

export type RenderArgs = {
  props: Record<string, any>;
  text?: VNode;
  children?: ComponentChildren;
  attrs: Record<string, string>;
};

export type Component = {
  label: string;
  props: Record<string, Prop>;
  text?: boolean;
  children?: boolean;
  // Holds blocks only sometimes (a list item's sublist), so an empty one
  // asks for none.
  optional?: boolean;
  // Runs code in the browser, so a published page with one loads script.
  behaviour?: boolean;
  // The component's own look: its root's properties, which the style
  // pencil shows as the default, and rules for its inner parts.
  style?: Record<string, string>;
  css?: [selector: string, decls: string][];
  render: (a: RenderArgs) => VNode;
};

// A value that breaks a prop's schema reads as its default, so whatever a
// merge leaves, the component gets props it can draw.
export function cleanProps(c: { props: Record<string, Prop> }, given: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, p] of Object.entries(c.props)) out[k] = checkProp(p, given[k]) ? given[k] : p.default;
  return out;
}

export function checkProp(p: Prop, v: unknown): boolean {
  switch (p.kind) {
    case "string": return typeof v === "string" && v.length <= (p.max ?? 200);
    case "number": return typeof v === "number" && Number.isFinite(v) && v >= (p.min ?? -Infinity) && v <= (p.max ?? Infinity);
    case "enum": return typeof v === "string" && p.options.includes(v);
    case "boolean": return typeof v === "boolean";
  }
}

// What a value must be, for the message when one isn't.
export function describeProp(p: Prop): string {
  switch (p.kind) {
    case "string": return `text of at most ${p.max ?? 200} characters`;
    case "number": return `a number${p.min !== undefined ? ` from ${p.min}` : ""}${p.max !== undefined ? ` to ${p.max}` : ""}`;
    case "enum": return `one of ${p.options.join(", ")}`;
    case "boolean": return "true or false";
  }
}

function Counter({ attrs, label, start }: { attrs: Record<string, string>; label: string; start: number }) {
  const [n, setN] = useState(start);
  return (
    <div {...attrs} class="c-counter">
      <button type="button" contentEditable={false} onClick={() => setN(n + 1)}>{label}</button>
      <output>{n}</output>
    </div>
  );
}

function Tabs({ attrs, labels, children }: { attrs: Record<string, string>; labels: string; children: ComponentChildren }) {
  const [active, setActive] = useState(0);
  const panels = Array.isArray(children) ? children : [children];
  const names = labels.split(",").map((s) => s.trim());
  return (
    <div {...attrs} class="c-tabs">
      <div class="c-tabs-list" role="tablist" contentEditable={false}>
        {panels.map((_, i) => (
          <button type="button" role="tab" aria-selected={i === active} key={i} onClick={() => setActive(i)}>
            {names[i] || `Tab ${i + 1}`}
          </button>
        ))}
      </div>
      {panels.map((p, i) => (
        <div class="c-tabs-panel" role="tabpanel" hidden={i !== active} key={i}>{p}</div>
      ))}
    </div>
  );
}

function Signup({ attrs, label, button, required }: { attrs: Record<string, string>; label: string; button: string; required: boolean }) {
  const [said, setSaid] = useState("");
  const [value, setValue] = useState("");
  return (
    <form
      {...attrs}
      class="c-signup"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (!value && !required) return setSaid("");
        setSaid(/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? "Thanks: you're on the list." : "Enter an email address, like you@example.com.");
      }}
    >
      <label contentEditable={false}>
        <span>{label}</span>
        <input type="email" value={value} onInput={(e) => setValue((e.target as HTMLInputElement).value)} />
      </label>
      <button type="submit" contentEditable={false}>{button}</button>
      <output role="status">{said}</output>
    </form>
  );
}

// shadcn's button, on the page's tokens: the counter's, the sign-up's and
// the button component's.
const BUTTON = "display:inline-flex;align-items:center;justify-content:center;height:36px;font:inherit;font-size:14px;font-weight:500;padding:0 16px;border-radius:calc(var(--radius) - 2px);border:1px solid transparent;background:var(--accent);color:white;cursor:pointer;text-decoration:none;box-shadow:0 1px 2px rgb(0 0 0 / .08)";

export const components: Record<string, Component> = {
  section: {
    label: "Section",
    props: {},
    children: true,
    style: { margin: "1.4em 0" },
    render: ({ attrs, children }) => <section {...attrs} class="c-section">{children}</section>,
  },
  heading: {
    label: "Heading",
    props: { level: { kind: "enum", default: "2", options: ["1", "2", "3", "4", "5", "6"] } },
    text: true,
    style: { margin: "1.2em 0 .4em" },
    css: [
      [":first-child", "margin-top:0"],
      ["h1", "font-size:clamp(30px, 5vw, 42px)"],
      ["h2", "font-size:26px"],
      ["h3", "font-size:20px"],
      ["h4", "font-size:17px"],
      ["h5", "font-size:15px"],
      ["h6", "font-size:14px"],
    ],
    render: ({ attrs, props, text }) => {
      const H = `h${props.level}` as "h1";
      return <H {...attrs} class="c-heading">{text}</H>;
    },
  },
  paragraph: {
    label: "Paragraph",
    props: {},
    text: true,
    style: { margin: "0 0 .8em" },
    render: ({ attrs, text }) => <p {...attrs} class="c-paragraph">{text}</p>,
  },
  // What a markdown file holds besides headings and paragraphs (markdown.ts).
  list: {
    label: "List",
    props: { ordered: { kind: "boolean", default: false } },
    children: true,
    style: { margin: "0 0 .8em", "padding-left": "1.4em" },
    render: ({ attrs, props, children }) =>
      props.ordered ? <ol {...attrs} class="c-list">{children}</ol> : <ul {...attrs} class="c-list">{children}</ul>,
  },
  item: {
    label: "List item",
    props: {},
    text: true,
    children: true,
    optional: true,
    style: { margin: ".2em 0" },
    render: ({ attrs, text, children }) => <li {...attrs} class="c-item">{text}{children}</li>,
  },
  code: {
    label: "Code",
    props: { lang: { kind: "string", default: "", max: 40 } },
    text: true,
    style: { margin: "0 0 .8em", padding: "var(--space-2)", "border-radius": "var(--radius)", "background-color": "var(--surface)" },
    css: [[" .tx", "font-family:ui-monospace,monospace;font-size:14px"]],
    render: ({ attrs, props, text }) => <pre {...attrs} class="c-code" data-lang={props.lang || undefined}><code>{text}</code></pre>,
  },
  table: {
    label: "Table",
    props: {},
    children: true,
    style: { margin: "0 0 .8em" },
    render: ({ attrs, children }) => <div {...attrs} class="c-table" role="table">{children}</div>,
  },
  row: {
    label: "Table row",
    props: {},
    children: true,
    render: ({ attrs, children }) => <div {...attrs} class="c-row" role="row">{children}</div>,
  },
  cell: {
    label: "Table cell",
    props: {},
    text: true,
    style: { padding: "4px 10px" },
    render: ({ attrs, text }) => <div {...attrs} class="c-cell" role="cell">{text}</div>,
  },
  // Lines this editor doesn't model (front matter, a quote, HTML), edited
  // as they are written.
  markdown: {
    label: "Markdown",
    props: {},
    text: true,
    style: { margin: "0 0 .8em", padding: "var(--space-2)", "border-radius": "var(--radius)" },
    css: [[" .tx", "font-family:ui-monospace,monospace;font-size:13px"]],
    render: ({ attrs, text }) => <pre {...attrs} class="c-markdown">{text}</pre>,
  },
  callout: {
    label: "Callout",
    props: { tone: { kind: "enum", default: "note", options: ["note", "warn"] } },
    children: true,
    style: {
      margin: "1.2em 0",
      padding: "var(--space-3)",
      "border-radius": "var(--radius)",
      "background-color": "var(--surface)",
      "border-left": "3px solid var(--accent)",
    },
    css: [[".tone-warn", "border-left-color:var(--warn)"]],
    render: ({ attrs, props, children }) => <aside {...attrs} class={`c-callout tone-${props.tone}`}>{children}</aside>,
  },
  counter: {
    label: "Counter",
    behaviour: true,
    props: {
      label: { kind: "string", default: "Count", max: 40 },
      start: { kind: "number", default: 0, min: -1e6, max: 1e6 },
    },
    style: { display: "flex", "align-items": "center", gap: "12px", margin: ".6em 0 1em" },
    css: [
      [" button", BUTTON],
      [" output", "font-weight:600;font-size:18px;font-family:ui-monospace,monospace;min-width:2ch"],
    ],
    render: ({ attrs, props }) => <Counter attrs={attrs} label={props.label} start={props.start} />,
  },
  tabs: {
    label: "Tabs",
    behaviour: true,
    children: true,
    props: { labels: { kind: "string", default: "One, Two", max: 200 } },
    style: { margin: "1em 0" },
    // shadcn's tabs: a sunken list, the open tab raised in it.
    css: [
      [" .c-tabs-list", "display:inline-flex;gap:2px;padding:3px;border-radius:var(--radius);background:var(--surface);margin-bottom:var(--space-2)"],
      [" .c-tabs-list button", "font:inherit;font-size:14px;font-weight:500;padding:4px 12px;border:1px solid transparent;border-radius:calc(var(--radius) - 2px);background:none;color:var(--muted);cursor:pointer"],
      [" .c-tabs-list button[aria-selected=true]", "color:var(--ink);background:light-dark(white, var(--line));border-color:var(--line);box-shadow:0 1px 2px rgb(0 0 0 / .08)"],
    ],
    render: ({ attrs, props, children }) => <Tabs attrs={attrs} labels={props.labels}>{children}</Tabs>,
  },
  signup: {
    label: "Sign-up form",
    behaviour: true,
    props: {
      label: { kind: "string", default: "Email", max: 60 },
      button: { kind: "string", default: "Sign up", max: 40 },
      required: { kind: "boolean", default: true },
    },
    style: { display: "flex", "flex-wrap": "wrap", "align-items": "end", gap: "8px", margin: ".6em 0 1em" },
    css: [
      [" label", "display:flex;flex-direction:column;gap:4px;font-size:14px;color:var(--muted)"],
      [" input", "font:inherit;font-size:15px;height:36px;padding:0 12px;border-radius:calc(var(--radius) - 2px);border:1px solid var(--line);background:none;color:var(--ink);min-width:16rem;box-shadow:0 1px 2px rgb(0 0 0 / .05)"],
      [" button", BUTTON],
      [" output", "flex-basis:100%;font-size:14px"],
    ],
    render: ({ attrs, props }) => <Signup attrs={attrs} label={props.label} button={props.button} required={props.required} />,
  },
  // shadcn's card, alert, badge and button, drawn from the page's tokens so
  // a published page needs nothing but its own styles.
  card: {
    label: "Card",
    props: {},
    children: true,
    style: {
      margin: "1.2em 0",
      padding: "var(--space-3)",
      "border-width": "1px",
      "border-style": "solid",
      "border-color": "var(--line)",
      "border-radius": "calc(var(--radius) + 4px)",
    },
    // Its first and last blocks sit flush with its padding; in the editor
    // each is inside a block's box.
    css: [
      ["", "box-shadow:0 1px 3px rgb(0 0 0 / .08)"],
      [" > :first-child", "margin-top:0"], [" > .block:first-child > *", "margin-top:0"],
      [" > :last-child", "margin-bottom:0"], [" > .block:last-child > *", "margin-bottom:0"],
    ],
    render: ({ attrs, children }) => <div {...attrs} class="c-card">{children}</div>,
  },
  alert: {
    label: "Alert",
    props: {
      title: { kind: "string", default: "Heads up", max: 120 },
      tone: { kind: "enum", default: "default", options: ["default", "destructive"] },
    },
    text: true,
    style: {
      margin: "1em 0",
      padding: "12px 16px",
      "border-width": "1px",
      "border-style": "solid",
      "border-color": "var(--line)",
      "border-radius": "var(--radius)",
      "font-size": "15px",
    },
    css: [
      [" .c-alert-title", "display:block;font-weight:600;margin-bottom:2px;color:var(--ink)"],
      [" .tx", "color:var(--muted)"],
      [".tone-destructive", "border-color:color-mix(in oklch, oklch(58% 0.22 27) 45%, transparent)"],
      [".tone-destructive .c-alert-title", "color:oklch(58% 0.22 27)"],
    ],
    render: ({ attrs, props, text }) => (
      <div {...attrs} class={`c-alert tone-${props.tone}`} role={props.tone === "destructive" ? "alert" : "note"}>
        <strong class="c-alert-title" contentEditable={false}>{props.title}</strong>
        {text}
      </div>
    ),
  },
  badge: {
    label: "Badge",
    props: { variant: { kind: "enum", default: "default", options: ["default", "secondary", "outline"] } },
    text: true,
    style: { margin: "0 0 .8em", "font-size": "12px", "font-weight": "500" },
    css: [
      ["", "display:block;width:fit-content;padding:2px 8px;border-radius:calc(var(--radius) - 2px);border:1px solid transparent;background:var(--accent);color:white;line-height:1.4"],
      [".v-secondary", "background:var(--surface);color:var(--ink)"],
      [".v-outline", "background:none;color:var(--ink);border-color:var(--line)"],
    ],
    render: ({ attrs, props, text }) => <span {...attrs} class={`c-badge v-${props.variant}`}>{text}</span>,
  },
  button: {
    label: "Button",
    props: {
      href: { kind: "string", default: "#", max: 500 },
      variant: { kind: "enum", default: "default", options: ["default", "outline", "secondary"] },
    },
    text: true,
    style: { margin: ".6em 0 1em" },
    css: [
      ["", "display:block;width:fit-content"],
      [" .c-button-link", BUTTON],
      [".v-outline .c-button-link", "background:none;color:var(--ink);border-color:var(--line)"],
      [".v-secondary .c-button-link", "background:var(--surface);color:var(--ink);box-shadow:none"],
    ],
    // A link drawn as a button: where it goes is a prop, never `javascript:`.
    render: ({ attrs, props, text }) => (
      <div {...attrs} class={`c-button v-${props.variant}`}>
        <a class="c-button-link" href={safeHref(props.href)}>{text}</a>
      </div>
    ),
  },
};

// Every component's own styles as one stylesheet's text, under `scope`.
// These are ours, from the source, so they are written as they are.
export function componentCss(scope = ".page"): string {
  const out: string[] = [];
  for (const [name, c] of Object.entries(components)) {
    const root = `${scope} .c-${name}`;
    if (c.style) out.push(`${root}{${Object.entries(c.style).map(([k, v]) => `${k}:${v}`).join(";")}}`);
    for (const [sel, decls] of c.css ?? []) {
      // A selector starting with a tag or a pseudo-class names the root
      // itself (h1.c-heading); one starting with a space, a part inside it.
      const s = sel.startsWith(" ") ? `${root}${sel}` : /^[a-z]/.test(sel) ? `${scope} ${sel}.c-${name}` : `${root}${sel}`;
      out.push(`${s}{${decls}}`);
    }
  }
  return out.join("\n");
}
